import React, { memo } from 'react';
import { Col, Row } from '../../lib/utils';
import dayjs from 'dayjs';
import { PeriodNavigatorType } from '../Date/PeriodNavigator';

type Props = {
    currentDate: string;
    onClickPrev?: (type: PeriodNavigatorType) => void;
    onClickNext?: (type: PeriodNavigatorType) => void;
    onClickType?: (type: PeriodNavigatorType) => void;
    type: PeriodNavigatorType;
    monthLength?: number;
    yearLength?: number;
    allLength?: number;
    hideTitle?: boolean;
    onResetPeriod?: () => void;
};

const MemberClassHistoryComponentsHeader = ({
    currentDate,
    onClickPrev,
    onClickNext,
    onClickType,
    type,
    monthLength = 0,
    yearLength = 0,
    allLength = 0,
    hideTitle,
    onResetPeriod,
}: Props) => {
    const count = type === 'thisYear' ? yearLength : type === 'all' ? allLength : monthLength;
    const isMonth = type === 'thisMonth';
    const isYear = type === 'thisYear';
    const isAll = type === 'all';

    const dateLabel = isMonth
        ? dayjs(currentDate).format('YYYY년 M월')
        : isYear
          ? dayjs(currentDate).format('YYYY년')
          : '전체 기간';

    const segments: Array<{ key: PeriodNavigatorType; label: string; active: boolean }> = [
        { key: 'thisMonth', label: '이번 달', active: isMonth },
        { key: 'thisYear', label: '이번 연도', active: isYear },
        { key: 'all', label: '전체', active: isAll },
    ];

    return (
        <Col style={{ marginBottom: 20 }}>
            {!hideTitle && (
                <Row style={{ alignItems: 'baseline', gap: 10, marginBottom: 14 }}>
                    <span style={{ fontSize: 20, fontWeight: 800, color: '#111827', letterSpacing: -0.4 }}>
                        수업 기록
                    </span>
                    <span
                        style={{
                            padding: '3px 10px',
                            borderRadius: 999,
                            backgroundColor: '#EFF6FF',
                            border: '1px solid #BFDBFE',
                            fontSize: 12,
                            fontWeight: 700,
                            color: '#1D4ED8',
                        }}
                    >
                        총 {count.toLocaleString()}회
                    </span>
                </Row>
            )}

            {/* 필터 바 */}
            <Row
                style={{
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: 12,
                    boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
                    flexWrap: 'wrap',
                }}
            >
                {/* 세그먼트 컨트롤 */}
                <Row
                    style={{
                        display: 'inline-flex',
                        padding: 3,
                        borderRadius: 9,
                        backgroundColor: '#F1F5F9',
                        gap: 2,
                    }}
                >
                    {segments.map((seg) => (
                        <button
                            key={seg.key}
                            onClick={() => onClickType?.(seg.key)}
                            style={{
                                padding: '6px 14px',
                                borderRadius: 7,
                                fontSize: 12,
                                fontWeight: seg.active ? 700 : 500,
                                backgroundColor: seg.active ? '#FFFFFF' : 'transparent',
                                color: seg.active ? '#1D4ED8' : '#64748B',
                                border: 'none',
                                cursor: 'pointer',
                                boxShadow: seg.active ? '0 2px 6px rgba(15,23,42,0.08)' : 'none',
                                transition: 'all 0.15s',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {seg.label}
                        </button>
                    ))}
                </Row>

                {/* 날짜 네비게이터 */}
                <Row
                    style={{
                        alignItems: 'center',
                        gap: 2,
                        padding: '2px 6px',
                        backgroundColor: '#F8FAFC',
                        borderRadius: 9,
                        border: '1px solid #E2E8F0',
                        opacity: isAll ? 0.6 : 1,
                    }}
                >
                    <button
                        onClick={() => onClickPrev?.(type)}
                        disabled={isAll}
                        style={{
                            width: 26,
                            height: 26,
                            borderRadius: 6,
                            border: 'none',
                            background: 'transparent',
                            color: isAll ? '#CBD5E1' : '#475569',
                            fontSize: 15,
                            fontWeight: 700,
                            cursor: isAll ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        ‹
                    </button>
                    <span
                        style={{
                            minWidth: 100,
                            textAlign: 'center',
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#1F2937',
                            padding: '0 6px',
                        }}
                    >
                        {dateLabel}
                    </span>
                    <button
                        onClick={() => onClickNext?.(type)}
                        disabled={isAll}
                        style={{
                            width: 26,
                            height: 26,
                            borderRadius: 6,
                            border: 'none',
                            background: 'transparent',
                            color: isAll ? '#CBD5E1' : '#475569',
                            fontSize: 15,
                            fontWeight: 700,
                            cursor: isAll ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        ›
                    </button>
                </Row>

                {/* 초기화 */}
                {onResetPeriod && (
                    <button
                        onClick={onResetPeriod}
                        style={{
                            padding: '6px 10px',
                            borderRadius: 8,
                            border: '1px solid #E2E8F0',
                            backgroundColor: '#FFFFFF',
                            color: '#64748B',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <span style={{ fontSize: 11 }}>↺</span>
                        초기화
                    </button>
                )}

                <div style={{ flex: 1 }} />

                {hideTitle && (
                    <span
                        style={{
                            padding: '3px 10px',
                            borderRadius: 999,
                            backgroundColor: '#EFF6FF',
                            border: '1px solid #BFDBFE',
                            fontSize: 12,
                            fontWeight: 700,
                            color: '#1D4ED8',
                        }}
                    >
                        총 {count.toLocaleString()}회
                    </span>
                )}
            </Row>
        </Col>
    );
};

export default memo(MemberClassHistoryComponentsHeader);
